import {
    IAccountMappingTemplateGroup,
    IReclassificationAccountRef,
    IReclassificationTransaction,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { GroupingPathHelper } from './GroupingPathHelper';
import { MaskingHelper } from './MaskingHelper';

/**
 * REV-21512: the view-model statics that used to live on `GroupingMaskingComponent`.
 * Moved here so utils-level consumers (`FxTranslationHelper`) can use them without importing
 * the component — `grouping-masking.component` itself imports `utils/*`, so a component import
 * from utils would be circular. The component keeps thin delegating statics, its public API
 * is unchanged.
 */
export class GroupingMaskingHelper {
    public static deepClone<T>(source: T): T {
        return JSON.parse(JSON.stringify(source));
    }

    /**
     * Helper to distinguish Groups from Items in Template
     */
    public static isGroupNode(item: any): boolean {
        return item && 'sortedItems' in item;
    }

    /**
     * REV-21342: SubFLI / Of-That rows are STRUCTURE, defined and ordered in the Account-Mapping
     * step. They are excluded from every sorting mechanism in this step (manual drag, the Sort
     * button, and saved sorting state) — only standard accounts may be re-ordered here.
     */
    public static isStructuralRow(item: any): boolean {
        return item?.type === ItemType.SUB_FLI || item?.type === ItemType.OF_THAT_ITEM;
    }

    /**
     * Sorts only the ACCOUNT entries of a FLI child list with `compare`, keeping structural rows
     * (SubFLIs / Of-That items) pinned at their template-defined positions.
     */
    public static sortPreservingStructuralRows(list: any[], compare: (a: any, b: any) => number): void {
        if (!list || list.length === 0) return;
        const structuralAt = new Set<number>();
        const movable: any[] = [];
        list.forEach((item, i) => {
            if (GroupingMaskingHelper.isStructuralRow(item)) structuralAt.add(i);
            else movable.push(item);
        });
        if (movable.length === 0) return;
        movable.sort(compare);
        let m = 0;
        for (let i = 0; i < list.length; i++) {
            if (!structuralAt.has(i)) list[i] = movable[m++];
        }
    }

    /**
     * Recursively ensures all accounts arrays are initialized (never null/undefined)
     * This prevents template errors when iterating over accounts
     */
    public static ensureAccountsArrays(group: IAccountMappingTemplateGroup | any): void {
        if (!group) return;

        // Traverse Groups
        if (group.groups) {
            group.groups.forEach((g: any) => GroupingMaskingHelper.ensureAccountsArrays(g));
        }

        // Traverse Items (FLIs)
        if (group.sortedItems) {
            group.sortedItems.forEach((item: any) => {
                if (GroupingMaskingHelper.isGroupNode(item)) {
                    GroupingMaskingHelper.ensureAccountsArrays(item);
                } else {
                    // Ensure accounts array exists
                    if (item.accounts === null || item.accounts === undefined) {
                        item.accounts = [];
                    }
                    // Also check Sub-FLIs within accounts
                    GroupingMaskingHelper.ensureSubFliAccountsArrays(item.accounts);
                }
            });
        }
    }

    /**
     * Ensures accounts arrays exist for Sub-FLIs within an accounts array
     */
    public static ensureSubFliAccountsArrays(accounts: any[]): void {
        if (!accounts) return;

        accounts.forEach((acc) => {
            if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                if (acc.accounts === null || acc.accounts === undefined) {
                    acc.accounts = [];
                }
                // Recursive for nested Sub-FLIs (if any)
                GroupingMaskingHelper.ensureSubFliAccountsArrays(acc.accounts);
            }
        });
    }

    /**
     * Recursively traverses the Group structure.
     * If it finds an item (FLI/SubFLI) that has a matching entry in sortingState,
     * it reorders the item's accounts to match that state.
     */
    public static applySavedSorting(group: IAccountMappingTemplateGroup | any, sortingState: Record<string, string[]>): void {
        if (!group) return;

        // Traverse Groups
        if (group.groups) {
            group.groups.forEach((g: any) => GroupingMaskingHelper.applySavedSorting(g, sortingState));
        }

        // Traverse Items (FLIs)
        if (group.sortedItems) {
            group.sortedItems.forEach((item: any) => {
                if (GroupingMaskingHelper.isGroupNode(item)) {
                    GroupingMaskingHelper.applySavedSorting(item, sortingState);
                } else {
                    GroupingMaskingHelper.applySortingToItem(item, sortingState);
                }
            });
        }
    }

    public static applySortingToItem(item: any, sortingState: Record<string, string[]>): void {
        if (!item.accounts || item.accounts.length === 0) return;

        // Check all possible path patterns that might match this item
        // Since we now use full paths, we need to find any matching key
        const matchingKeys = Object.keys(sortingState).filter((key) =>
            key.includes(`__fli__${GroupingPathHelper.sanitizeId(item.name || item.caption || '')}`),
        );

        matchingKeys.forEach((key) => {
            const savedOrderIds = sortingState[key];
            if (savedOrderIds && savedOrderIds.length > 0) {
                // FIX: Sort using Identifier
                // REV-21342: only ACCOUNT rows follow the saved order; structural rows (SubFLIs /
                // Of-That items) stay pinned at their mapping-defined template positions — even
                // if a stale sorting state (from before this fix) still lists them.
                GroupingMaskingHelper.sortPreservingStructuralRows(item.accounts, (a: any, b: any) => {
                    const indexA = GroupingMaskingHelper.savedOrderIndexOf(a, savedOrderIds);
                    const indexB = GroupingMaskingHelper.savedOrderIndexOf(b, savedOrderIds);

                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;

                    return indexA - indexB;
                });
            }
        });

        // Recursion for Sub-FLIs
        item.accounts.forEach((acc: any) => {
            if ((acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') && acc.accounts) {
                GroupingMaskingHelper.applySortingToSubFli(acc, sortingState);
            }
        });
    }

    /**
     * Position of a row in a saved order: its own identifier first, then its number + name.
     * -1 when neither is listed.
     */
    public static savedOrderIndexOf(row: any, savedOrderIds: string[]): number {
        const isGroupRow = row?.groupId && Array.isArray(row.accounts);
        const candidates = isGroupRow || !row
            ? [GroupingMaskingHelper.accountIdentifierOf(row)]
            : MaskingHelper.getIdentifierCandidates(row);
        for (const id of candidates) {
            const index = savedOrderIds.indexOf(id);
            if (index !== -1) return index;
        }
        return -1;
    }

    public static applySortingToSubFli(subFli: any, sortingState: Record<string, string[]>): void {
        if (!subFli.accounts || subFli.accounts.length === 0) return;

        const matchingKeys = Object.keys(sortingState).filter((key) =>
            key.includes(`__subfli__${GroupingPathHelper.sanitizeId(subFli.name || subFli.caption || '')}`),
        );

        matchingKeys.forEach((key) => {
            const savedOrderIds = sortingState[key];
            if (savedOrderIds && savedOrderIds.length > 0) {
                // REV-21342: accounts inside a SubFLI may be re-ordered, but nested structural
                // rows stay pinned (same rule as at FLI level).
                GroupingMaskingHelper.sortPreservingStructuralRows(subFli.accounts, (a: any, b: any) => {
                    const indexA = GroupingMaskingHelper.savedOrderIndexOf(a, savedOrderIds);
                    const indexB = GroupingMaskingHelper.savedOrderIndexOf(b, savedOrderIds);

                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;

                    return indexA - indexB;
                });
            }
        });

        // Recursive for nested Sub-FLIs
        subFli.accounts.forEach((acc: any) => {
            if ((acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') && acc.accounts) {
                GroupingMaskingHelper.applySortingToSubFli(acc, sortingState);
            }
        });
    }

    /**
     * REV-20899: Build a Set of composite keys (`accountNumber|name`) for every
     * account referenced as SOURCE or TARGET in the current transaction list.
     * Used to detect orphaned reclasses accounts.
     */
    public static buildReferencedReclassKeys(
        transactions: IReclassificationTransaction[],
        pendingNewReclassesAccounts: IReclassificationAccountRef[] = [],
    ): Set<string> {
        const keys = new Set<string>();
        for (const t of transactions ?? []) {
            // Key format must match GroupingHelper.pruneOrphanedReclassesAccounts
            // (`${accountNumber}|${name ?? ''}`) so an undefined name never
            // produces a mismatched key that wrongly prunes a phantom.
            const srcTwoPart = `${t.source.accountNumber}|${t.source.name ?? ''}`;
            const tgtTwoPart = `${t.target.accountNumber}|${t.target.name ?? ''}`;
            keys.add(srcTwoPart);
            keys.add(tgtTwoPart);
            // Also add UUID-qualified keys when available.
            if (t.source.uniqueAccountId) {
                keys.add(`${srcTwoPart}|${t.source.uniqueAccountId}`);
            }
            if (t.target.uniqueAccountId) {
                keys.add(`${tgtTwoPart}|${t.target.uniqueAccountId}`);
            }
        }
        // REV-20899: protect session-pending phantom accounts from being pruned
        // by `pruneOrphanedReclassesAccounts` before the user has had a chance
        // to assign a transaction to the newly-created account. Without this,
        // the phantom is added by `applyNewReclassesAccounts` and then
        // immediately removed in the same `renderTable()` call because no
        // transaction references it yet — making it impossible to select it in
        // the reclassification modal's target dropdown.
        for (const p of pendingNewReclassesAccounts ?? []) {
            keys.add(`${p.accountNumber}|${p.name ?? ''}`);
        }
        return keys;
    }

    public static accountIdentifierOf(account: any): string {
        if (!account) return '';

        // FIX: Prioritize groupId for Groups to ensure unique IDs for Split Counterparts
        // (Split counterparts have shared accountNumber but unique groupId)
        if (account.groupId && Array.isArray(account.accounts)) {
            return MaskingHelper.getAccountIdentifier(account.groupId, account.name, account.shareholder);
        }

        // Row-scoped for a TRUE DUPLICATE (see MaskingHelper.getRowIdentifier and
        // ensureDuplicateRowIdentities), the plain 2-part identifier for every other account. This
        // is the single place the whole step derives row identity from, so selection, grouping,
        // renaming, the drag order and trackBy all address ONE row at a time.
        return MaskingHelper.getRowIdentifier(account);
    }

    /**
     * REV-20899: "final" CY value (imported closing balance + net closing reclass).
     * For an aggregate this is the sum of children's `calculatedClosingBalance`
     * plus the sum of children's `closingReClass` — already aggregated by the
     * engine into the aggregate's own `closingReClass`.
     */
    public static finalCYOf(item: { calculatedClosingBalance?: number | null; closingReClass?: number | null } | null | undefined): number {
        if (item == null) return 0;
        return (item.calculatedClosingBalance ?? 0) + (item.closingReClass ?? 0);
    }

    /** REV-20899: "final" PY value (imported opening balance + net opening reclass). */
    public static finalPYOf(item: { calculatedOpeningBalance?: number | null; openingReClass?: number | null } | null | undefined): number {
        if (item == null) return 0;
        return (item.calculatedOpeningBalance ?? 0) + (item.openingReClass ?? 0);
    }
}
